/**
 * Microsoft OAuth2 Token Management
 * 
 */
export interface CodeValidationResponse {
  data: {
    code: string;
    status: string;
  };
}

export interface CodeUpdateResponse {
  data: {
    code: string;
    status: string;
  };
}

export interface CodeValidationError {
  error: string;
  message?: string;
}

/**
 * Validate a code using Microsoft Power Automate API
 * This function calls our Next.js API route which handles authentication and validation
 * 
 * @param code - The code to validate
 * @returns Promise with the validation response or throws an error
 */
export async function validateCode(code: string): Promise<CodeValidationResponse> {
  try {
    const response = await fetch('/api/auth/microsoft-validate-code', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ code }),
    });

    if (!response.ok) {
      const errorData: CodeValidationError = await response.json();
      throw new Error(
        errorData.message || 
        errorData.error || 
        `Failed to validate code: ${response.statusText}`
      );
    }

    const data: CodeValidationResponse = await response.json();
    return data;
  } catch (error) {
    console.error('Error validating code:', error);
    throw error;
  }
}

/**
 * Update a code's status using Microsoft Power Automate API
 * This function calls our Next.js API route which handles authentication and status update
 * 
 * @param code - The code to update
 * @param status - The new status (e.g., 'completed', 'active')
 * @returns Promise with the update response or throws an error
 */
export async function updateCodeStatus(code: string, status: string): Promise<CodeUpdateResponse> {
  try {
    const response = await fetch('/api/auth/microsoft-update-code-status', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ code, status }),
    });

    if (!response.ok) {
      const errorData: CodeValidationError = await response.json();
      throw new Error(
        errorData.message || 
        errorData.error || 
        `Failed to update code status: ${response.statusText}`
      );
    }

    const data: CodeUpdateResponse = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating code status:', error);
    throw error;
  }
}
